import type { google } from '@googlemaps/places/build/protos/protos'
import { orderBy, uniq } from 'lodash-es'
import type { LatLng } from '../geography/types.ts'
import { parseGeom, type GeoJSON } from '../geography/parseGeom.ts'
import { addressSchema, type Address, type AssetAddress, type ParsedAddress } from './types.ts'

type IAddressComponent = google.maps.places.v1.Place.IAddressComponent

export function parseAddressGeom(geom: unknown): LatLng | null {
  return (typeof geom === 'string' && geom.includes('POINT')) ||
    (!!geom && typeof geom === 'object' && 'type' in geom && geom.type === 'Point')
    ? (parseGeom(geom as string | GeoJSON) as LatLng | null)
    : null
}

export function addressIsComplete(address: Address | undefined) {
  return !!address?.street && !!address.street_number
}

export function validateAddress(address: unknown): address is Address {
  return !!address && addressSchema.safeParse(address).success
}

export function normalizeAddress<T extends Address | AssetAddress>(address: T): T {
  const normalized = {
    ...address,
    street: address.street?.trim() || '',
    street_number: address.street_number?.trim() || '',
    postal_code: address.postal_code?.trim() || '',
    city: address.city?.trim() || '',
    country: address.country?.trim() || 'Finland',
  }

  if ('unit_number' in normalized) {
    normalized.unit_number = (address as AssetAddress).unit_number?.trim() || ''
  }

  if (normalized.municipality) {
    normalized.municipality = normalized.municipality.trim()
  }

  if (normalized.region) {
    normalized.region = normalized.region.trim()
  }

  if (normalized.state) {
    normalized.state = normalized.state.trim()
  }

  if (normalized.neighborhood) {
    normalized.neighborhood = normalized.neighborhood.trim()
  }

  return normalized as T
}

export function mergeAddressPreservingOriginal(original: Address, geocoded: AssetAddress): Address {
  return {
    street: original.street?.trim() || geocoded.street,
    street_number: original.street_number?.trim() || geocoded.street_number,
    postal_code: original.postal_code?.trim() || geocoded.postal_code,
    city: original.city?.trim() || geocoded.city,
    municipality: original.municipality?.trim() || geocoded.municipality,
    region: original.region?.trim() || geocoded.region,
    state: original.state?.trim() || geocoded.state,
    country: original.country?.trim() || geocoded.country,
    neighborhood: original.neighborhood?.trim() || geocoded.neighborhood,
  }
}

export function createFullAddressForAsset(parsedAddress: ParsedAddress, assetId: string) {
  const address = parsedAddress.address
  const addressConnections = parsedAddress.address_connections.filter(
    (connection) => connection.asset_id === assetId
  )

  const unitNumber =
    orderBy(addressConnections, ['updated_at', 'created_at'], ['desc', 'desc'])
      .map((connection) => connection.data?.unitNumber)
      .filter(Boolean)?.[0] ?? undefined

  if (unitNumber === address.street_number) {
    return address
  }

  return {
    ...address,
    unit_number: unitNumber,
  } satisfies AssetAddress
}

export function isAddress(address: unknown): address is Address {
  return addressSchema.safeParse(address).success
}

export function formatAddressComponents(address: AssetAddress): string {
  return uniq([
    [address.street, address.street_number].filter(Boolean).join(' '),
    address.postal_code,
    address.city,
    address.municipality,
    address.region,
    address.state,
    address.country,
  ])
    .map((item) => item?.trim() ?? '')
    .filter(Boolean)
    .join(', ')
}

export function addressAreaItems(
  address: AssetAddress,
  pickKeys: (keyof AssetAddress)[] = [],
  maxItems = pickKeys.length
) {
  return pickKeys.reduce((acc, key) => {
    if (acc.length >= maxItems) {
      return acc
    }

    const value = address[key]

    if (value && !acc.includes(value)) {
      if (key === 'country' && acc.length >= 2) {
        acc.push(value)
        return acc
      }
      if (acc.length < 4 && key !== 'municipality' && key !== 'region' && key !== 'state') {
        acc.push(value)
      } else if (acc.length < 3) {
        acc.push(value)
      }
    }

    return acc
  }, [] as string[])
}

export function simplifyAddressComponents(addressComponents: IAddressComponent[]): AssetAddress {
  const result: AssetAddress = {
    unit_number: '',
    street: '',
    street_number: '',
    neighborhood: '',
    postal_code: '',
    city: '',
    municipality: '',
    region: '',
    state: '',
    country: '',
  }

  const hasSubpremise = addressComponents.some((component) =>
    component.types?.includes('subpremise')
  )

  addressComponents.forEach((component) => {
    component.types?.forEach((type) => {
      const textField = 'longText' in component ? ('longText' as const) : ('long_name' as const)
      const componentValue = (
        component as {
          longText?: string
          long_name?: string
        }
      )[textField]

      switch (type) {
        case 'subpremise':
          result.unit_number = componentValue || ''
          break
        case 'street_number':
          if (componentValue) {
            // Match initial numbers until a space or non-digit
            const match = componentValue.match(/^(\d+)(.*)/)

            if (match && !hasSubpremise) {
              result.street_number = match[1]
              // Only set unit_number if there's remaining content and no subpremise
              const remaining = match[2].trim()

              if (remaining) {
                result.unit_number = remaining
              }
            } else {
              result.street_number = componentValue
            }
          }
          break
        case 'route':
          if (componentValue) {
            // Match if the street name ends with a space and number
            const match = componentValue.match(/^(.*?)\s+(\d+)$/)
            if (match && !result.street_number) {
              // [0] is full match, [1] is street name, [2] is number
              result.street = match[1]
              result.street_number = match[2]
            } else {
              result.street = componentValue
            }
          }
          break
        case 'locality':
          result.city = componentValue || ''
          break
        case 'administrative_area_level_1':
          result.state = componentValue || ''
          break
        case 'administrative_area_level_2':
          result.region = componentValue || ''
          break
        case 'administrative_area_level_3':
          result.municipality = componentValue || ''
          break
        case 'sublocality':
        case 'sublocality_level_1':
        case 'district':
        case 'neighborhood':
          result.neighborhood = componentValue || ''
          break
        case 'country':
          result.country = componentValue || ''
          break
        case 'postal_code':
          result.postal_code = componentValue || ''
          break
      }
    })
  })

  return result
}
